import services from "@src/Services";
import Colors from "@src/constants/Colors";

export const getMapHistoricalReportData = (
  response: HistoricalReportDataType,
  tab: string
) => {
  const chartTypeTab = "line";
  const data = {
    // mapping sales
    sales: {
      title: "Total Sales",
      chartType: chartTypeTab,
      firstColor: Colors.secondarypurple_500,
      secondColor: Colors.success_500,
      total: {
        title: `${tab} Total`,
        amount: response?.sales?.total,
        textColor: Colors.secondarypurple_600,
        backgroundColor: Colors.secondarypurple_100,
        font: "Ringside Regular-Bold",
      },
      plan: {
        title: `${tab} Plan`,
        amount: response?.sales?.plan,
        textColor: Colors.success_600,
        backgroundColor: Colors.success_200,
        font: "Ringside Regular-Bold",
      },
      firstDataset: response?.sales?.data,
      secondDataSet: response?.sales?.planData,
    },
    // mapping transaction
    transaction: {
      title: "Transactions",
      chartType: chartTypeTab,
      firstColor: Colors.secondary_rose_500,
      secondColor: Colors.primarygray_300,
      total: {
        title: `${tab} Total Tr`,
        amount: response?.transactions?.total,
        textColor: Colors.secondary_rose_500,
        backgroundColor: Colors.secondary_rose_100,
        font: "Ringside Regular-Bold",
        hideDollar: true,
      },
      plan: {
        title: `${tab} $ / Tr`,
        amount: response?.transactions?.avg,
        isFloat: true,
      },
      firstDataset: response?.transactions?.data,
    },
    // mapping stile
    stile: {
      title: "Stile",
      chartType: chartTypeTab,
      firstColor: Colors.secondary_blue_600_transparent,
      secondColor: Colors.primarygray_300,
      total: {
        title: `${tab} Net Stile`,
        amount: response?.stile?.net?.total,
        textColor: Colors.secondary_blue_600,
        backgroundColor: Colors.secondary_blue_100,
        font: "Ringside Regular-Bold",
        hideDollar: true,
      },
      plan: {
        title: `${tab} Gross Stile`,
        amount: response?.stile?.gross?.total,
        textColor: Colors.neutral_gray_500,
        backgroundColor: Colors.neutral_gray_100,
        font: "Ringside Regular-Bold",
        hideDollar: true,
      },
      netPerCap: {
        title: `${tab} Net Per Cap`,
        amount: response?.stile?.net?.perCap,
        isFloat: true,
      },
      grossPerCap: {
        title: `${tab} Gross Per Cap`,
        amount: response?.stile?.gross?.perCap,
        isFloat: true,
      },
      firstDataset: response?.stile?.net?.data,
      secondDataSet: response?.stile?.gross?.data,
    },
    // map guest
    guest: {
      title: "Guest Count",
      chartType: chartTypeTab,
      firstColor: Colors.secondary_orange_500,
      secondColor: Colors.primarygray_300,
      total: {
        title: "Total Guest Count",
        amount: response?.guest?.total,
        textColor: Colors.secondary_orange_500,
        backgroundColor: Colors.secondary_orange_100,
        font: "Ringside Regular-Bold",
        hideDollar: true,
      },
      plan: {
        title: `${tab} $ / Guest`,
        amount: response?.guest?.avg,
        isFloat: true,
      },
      firstDataset: response?.guest?.data,
    },
    // map labour
    labour: {
      title: "Labour",
      chartType: chartTypeTab,
      firstColor: Colors.primary_600,
      secondColor: Colors.success_500,
      annotationColor: Colors.secondarypink_600,
      total: {
        title: "Avg Labour",
        amount: response?.labour?.total?.act,
        textColor: Colors.secondarypink_600,
        backgroundColor: Colors.secondarypink_100,
        font: "Ringside Regular-Bold",
        pct: response?.labour?.total?.pct,
      },
      plan: {
        title: "Total Plan Labour",
        amount: response?.labour?.plan?.act,
        pct: response?.labour?.plan?.pct,
      },
      // total labour pct
      targetPlanValue: response?.labour?.total?.pct,
      // targetTotalValue: response?.labour?.total?.pct,
      firstDataset: response?.labour?.data,
      secondDataSet: response?.labour?.planData,
    },
    // map cogs
    cogs: {
      title: "COGS",
      chartType: chartTypeTab,
      firstColor: Colors.primary_600,
      secondColor: Colors.success_500,
      annotationColor: Colors.warning_500,
      total: {
        title: "Avg COGS",
        amount: response?.cogs?.total?.act,
        textColor: Colors.warning_600,
        backgroundColor: Colors.warning_100,
        font: "Ringside Regular-Bold",
        pct: response?.cogs?.total?.pct,
      },
      plan: {
        title: "Total Plan COGS",
        amount: response?.cogs?.plan?.act,
        pct: response?.cogs?.plan?.pct,
      },
      // total cogs pct
      targetPlanValue: response?.cogs?.total?.pct,
      // targetTotalValue: response?.cogs?.total?.pct,
      firstDataset: response?.cogs?.data,
      secondDataSet: response?.cogs?.planData,
    },
  };
  return data;
};

const getCustomExtraPathHistoricalReports = (
  filters: HistoricalReportsFilterType
) => {
  let path = "";
  const { property, store, rvc, date, filterType, tab, cumulative } =
    filters || {};

  if (property) {
    path += `?property=${property?.toString().trim()}`;
  }

  if (store) {
    path += `&store=${store?.toString().trim()}`;
  }

  if (rvc) {
    path += `&rvc=${rvc?.toString().trim()}`;
  }

  if (date) {
    path += `&date=${date?.toString().trim()}`;
  }

  if (filterType) {
    path += `&event_filter=${filterType?.toString().trim()}`;
  }

  if (tab) {
    path += `&filter_by=${tab?.toString().trim()}`;
  }

  if (cumulative) {
    path += `&cumulative=${cumulative?.toString().trim()}`;
  }

  let url = path;
  if (path.length && path[0] !== "?") {
    url = url.replace("&", "?");
  }

  return url;
};

const DataTotal = {
  data: [],
  total: null,
};

const Plan = {
  ...DataTotal,
  plan: null,
  planData: [],
};

const Average = {
  ...DataTotal,
  avg: null,
};

const StileNetGross = {
  data: [],
  total: null,
  perCap: null,
};

const CogsLabour = {
  act: null,
  pct: null,
};

export const initialHistoricalReportData: HistoricalReportDataType = {
  labels: [],
  sales: Plan,
  transactions: Average,
  stile: {
    net: StileNetGross,
    gross: StileNetGross,
  },
  guest: Average,
  cogs: {
    data: [],
    total: CogsLabour,
    plan: CogsLabour,
    planData: [],
  },
  labour: {
    data: [],
    total: CogsLabour,
    plan: CogsLabour,
    planData: [],
  },
};

export const getHistoricalReports = async (
  filters: HistoricalReportsFilterType
) => {
  try {
    const advancedFilterExtraPath = getCustomExtraPathHistoricalReports({
      ...filters,
    });

    const customPath = `${advancedFilterExtraPath}`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = await services.get(`/historical-report${customPath}`);

    if (!res.error) {
      return { data: res?.data?.data };
    }
    return { data: null, error: "Something went wrong !" };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    return {
      error: err?.error?.message || "Something went wrong!",
    };
  }
};
